import React from "react";
import { Search, Loader2 } from "lucide-react";
import {
  FormControl,
  TextField,
  InputAdornment,
  Autocomplete,
  Button,
  Box,
  Card,
  Typography,
  LinearProgress,
} from "@mui/material";
import sourceSystem from "../../jsonUtils/sourceSystemJson";

const LoadingProgress = ({ loading }) => {
  if (!loading) return null;

  return (
    <Box sx={{ width: "100%", mt: 2, mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, textAlign: "center" }}>
        מחפש נתונים במערכת...
      </Typography>
      <LinearProgress
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: "#e3f2fd",
          "& .MuiLinearProgress-bar": {
            backgroundColor: "#1976d2",
          },
        }}
      />
    </Box>
  );
};

const SearchForm = ({
  formData,
  entityTypes,
  loading,
  handleInputChange,
  setFormData,
  fetchLogs,
}) => {
  return (
    <Box>
      {/* Impressive Title */}
      <Typography
        variant="h3"
        component="h1"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          color: "#0563B0",
          mb: 3,
          fontSize: { xs: "2rem", md: "3rem" },
          background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        מערכת לוגים
      </Typography>

      <Card elevation={1} sx={{ p: 2, mb: 4, borderRadius: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          {/* Entity Number */}
          <FormControl>
            <TextField
              onChange={handleInputChange}
              label="מספר ישות"
              name="entityNumber"
              value={formData.entityNumber}
              variant="outlined"
              size="small"
              sx={{ width: 200 }}
            />
          </FormControl>

          {/* Entity Type */}
          <FormControl>
            <Autocomplete
              size="small"
              options={Object.entries(entityTypes)}
              getOptionLabel={(option) => option[1]}
              onChange={(event, value) => {
                setFormData((prev) => ({
                  ...prev,
                  entityType: value ? value[0] : "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="סוג ישות"
                  variant="outlined"
                />
              )}
              sx={{ width: 200 }}
            />
          </FormControl>

          {/* Source System */}
          <FormControl>
            <Autocomplete
              size="small"
              options={Object.entries(sourceSystem)}
              getOptionLabel={(option) => option[1]}
              onChange={(event, value) => {
                setFormData((prev) => ({
                  ...prev,
                  sourceSystem: value ? value[0] : "",
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="מערכת מקור"
                  variant="outlined"
                />
              )}
              sx={{ width: 200 }}
            />
          </FormControl>

          {/* Search Button */}
          <Button
            variant="contained"
            disabled={loading}
            onClick={!loading ? fetchLogs : undefined}
            startIcon={
              loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )
            }
            size="small"
            sx={{
              height: "40px",
              minWidth: "200px",
              backgroundColor: loading ? "#e3f2fd" : "#1976d2",
              color: loading ? "#1976d2" : "#fff",
              border: `1px solid ${loading ? "#1976d2" : "#1976d2"}`,
              borderRadius: "8px",
              fontWeight: "bold",
              fontSize: "16px",
              boxShadow: loading 
                ? "0px 4px 8px rgba(25, 118, 210, 0.3)" 
                : "0px 2px 4px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: loading ? "#e3f2fd" : "#1565c0",
                borderColor: loading ? "#1976d2" : "#1565c0",
              },
              "&:disabled": {
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
                borderColor: "#1976d2",
              },
            }}
          >
            {loading ? "מחפש נתונים..." : "חיפוש"}
          </Button>
        </Box>

        {/* Loading Progress - Shows below the form when loading */}
        <LoadingProgress loading={loading} />
      </Card>
    </Box>
  );
};

export default SearchForm;